import React from 'react';
import { Box, Link, Typography } from '@mui/material';
import BaseHeader from '../headers/BaseHeader';

const topInfoImageSrc = `${process.env.PUBLIC_URL}/top_info.png`;
const surveyUrl = 'https://forms.gle/cezGgxfFHshme5ZAA';

const pageSx = {
  minHeight: '100vh',
  bgcolor: '#f7f4ef',
  color: '#111827',
};

const contentSx = {
  width: '100%',
  maxWidth: '880px',
  mx: 'auto',
  px: { xs: 2, sm: 3 },
  py: { xs: 4, sm: 6 },
  boxSizing: 'border-box',
};

const sectionSx = {
  py: 2.25,
  borderTop: '1px solid rgba(17, 24, 39, 0.12)',
};

const sectionTextSx = {
  fontSize: '14px',
  lineHeight: 1.8,
  color: 'rgba(17, 24, 39, 0.72)',
};

const lineTextSx = {
  fontSize: '14px',
  lineHeight: 1.85,
  color: 'rgba(17, 24, 39, 0.76)',
};

const noticeTitleSx = {
  mt: 1.25,
  mb: 0.75,
  fontSize: '16px',
  lineHeight: 1.6,
  fontWeight: 900,
  color: '#111827',
};

const noticeSubTitleSx = {
  mt: 1.75,
  mb: 0.5,
  fontSize: '14px',
  lineHeight: 1.6,
  fontWeight: 900,
  color: '#111827',
};

const bulletRowSx = {
  display: 'grid',
  gridTemplateColumns: '1em minmax(0, 1fr)',
  columnGap: 0.25,
  alignItems: 'start',
  ...lineTextSx,
};

const dividerSx = {
  my: 1.5,
  borderTop: '1px solid rgba(17, 24, 39, 0.12)',
};

const headerLinkSx = {
  cursor: 'pointer',
  borderRadius: '3px',
  padding: '8px',
  width: '40px',
  transition: 'background-color 0.2s ease, color 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.12)',
  },
  '&:active': {
    backgroundColor: 'rgba(255, 255, 255, 0.24)',
  },
  '&:focus-visible': {
    outline: '2px solid #ffffff',
    outlineOffset: '2px',
  },
};

const betaSections = [
  {
    key: 'overview',
    title: '概要',
    items: [
      'ベータテスト開催！',
      '',
      'bettyは、誰でも予想ゲームを作って楽しめる参加型ベットゲームです。',
      '',
      '正式リリースに向けて、ベータテストを開催します。',
      '実際のスポーツ中継を題材に、勝敗予想やリアルタイム予想を体験しながら、サービスの改善にご協力ください。',
      'もちろん、自分でルームを作ってイベントを開催することも大歓迎です！',
      '',
    ],
  },
  {
    key: 'survey',
    title: 'アンケート',
    items: [
      'プレイ後にアンケートへの回答をお願いします。操作中に気になった点、分かりにくかった点、表示崩れなどを記録しておいてください。',
    ],
  },
  {
    key: 'notice',
    title: '注意事項',
    items: [
      '# ベータテスト参加にあたってのお願い',
      '',
      '## ベータ版について',
      '',
      '* 本サービスは開発中のベータ版です。',
      '* 不具合やメンテナンスにより、一時的にご利用いただけない場合があります。',

      '## データについて',
      '',
      '* ベータ版で獲得したポイントやランキングなどのデータは、正式リリース時や運用方針の変更時に削除またはリセットする場合があります。',

      '## 皆さまへのお願い',

      '* 不具合を発見した際は、アンケートなどからご報告いただけると幸いです。',
      '* 他の参加者の迷惑となる行為や、不具合を意図的に悪用する行為はご遠慮ください。',
      '* 複数アカウントの利用はご遠慮ください。',
      '* チャットやルーム作成では、公序良俗に反する内容や、他の参加者が不快となる内容の投稿はご遠慮ください。',

      '---',

      '皆さまからいただいたご意見をもとに、より良いサービスを目指して改善を進めてまいります。ご協力をよろしくお願いいたします。'

    ],
  },
];

const renderItemLine = (item, index) => {
  if (item === '') {
    return <Box key={`blank-${index}`} sx={{ height: '10px' }} />;
  }

  if (item === '---') {
    return <Box key={`divider-${index}`} sx={dividerSx} />;
  }

  if (item.startsWith('# ')) {
    return (
      <Typography key={`${item}-${index}`} component="h3" sx={noticeTitleSx}>
        {item.slice(2)}
      </Typography>
    );
  }

  if (item.startsWith('## ')) {
    return (
      <Typography key={`${item}-${index}`} component="h4" sx={noticeSubTitleSx}>
        {item.slice(3)}
      </Typography>
    );
  }

  if (item.startsWith('* ') || item.startsWith('・')) {
    const text = item.startsWith('* ') ? item.slice(2) : item.slice(1);

    return (
      <Box key={`${item}-${index}`} sx={bulletRowSx}>
        <Box component="span">・</Box>
        <Box component="span">{text}</Box>
      </Box>
    );
  }

  return (
    <Typography key={`${item}-${index}`} sx={lineTextSx}>
      {item}
    </Typography>
  );
};

function BetaTestPage() {
  return (
    <Box sx={pageSx}>
      <BaseHeader>
        <Box onClick={() => { window.location.href = '/'; }} sx={headerLinkSx}>
          TOP
        </Box>
      </BaseHeader>
      <Box component="main" sx={contentSx}>
        <Box sx={{ mb: 4 }}>
          <Typography component="h1" sx={{ fontSize: { xs: '28px', sm: '36px' }, fontWeight: 900, mb: 1.5 }}>
            Betty ベータテスト
          </Typography>
          <Typography sx={{ fontSize: '15px', lineHeight: 1.8, color: 'rgba(17, 24, 39, 0.72)' }}>
            Bettyのベータテストに参加する方向けのページです。
          </Typography>
        </Box>

        <Box
          component="img"
          src={topInfoImageSrc}
          alt="betty beta test information"
          sx={{
            display: 'block',
            width: '100%',
            height: 'auto',
            mb: 4,
            borderRadius: { xs: 1.5, sm: 2 },
            border: '1px solid rgba(17, 24, 39, 0.12)',
            boxShadow: '0 12px 32px rgba(15, 23, 42, 0.10)',
          }}
        />

        {betaSections.map((section) => (
          <Box key={section.key} sx={sectionSx}>
            <Typography component="h2" sx={{ fontSize: '18px', fontWeight: 900, mb: 1 }}>
              {section.title}
            </Typography>
            {section.body && (
              <Typography sx={sectionTextSx}>
                {section.body}
              </Typography>
            )}
            {section.items && (
              <Box>
                {section.key === 'survey' && (
                  <Typography sx={lineTextSx}>
                    アンケートは{' '}
                    <Link href={surveyUrl} target="_blank" rel="noopener noreferrer" underline="hover">
                      こちら
                    </Link>
                    から回答できます。
                  </Typography>
                )}
                {section.items.map((item, index) => renderItemLine(item, index))}
              </Box>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default BetaTestPage;
